/**
 * @fileoverview Página /users — lista densa de usuários no padrão visual
 * canônico (compartilhado com Empresas → Usuários). Toda a UI da tabela,
 * busca e paginação vem de `<UsersTable />` para evitar drift visual.
 *
 * Diferenças desta tela vs. tabela em Empresas:
 *  - Busca + paginação SERVER-SIDE: API `/users` aceita searchParam/pageNumber.
 *  - Super-admin enxerga botão "Acessar como" (impersonação) em cada linha.
 *  - "Conta principal" é resolvido a partir de `user.company.name` (já vem do
 *    backend), portanto não precisamos do mapa de contas.
 *  - Quem gere a equipe também convida por e-mail e vê os convites pendentes,
 *    acima da tabela (TeamInvites).
 */

import React, { useState, useEffect, useReducer, useContext, useCallback } from "react";
import { toast } from "react-toastify";
import { makeStyles } from "@material-ui/core/styles";
import { Mail, Plus } from "lucide-react";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import toastError from "../../errors/toastError";
import { useDebounce } from "../../hooks/useDebounce";
import { SocketContext } from "../../context/Socket/SocketContext";
import { AuthContext } from "../../context/Auth/AuthContext";
import usePermission, { isAdminUser } from "../../hooks/usePermission";
import { useBilling } from "../../context/Billing/BillingContext";
import UserDeleteImpactModal from "../UserDeleteImpactModal";
import UserEditorModal from "../UserEditorModal";
import UsersTable, { UsersTableSearch, UsersTablePagination } from "../UsersTable";
import InviteDialog from "../TeamInvites/InviteDialog";
import PendingInvites from "../TeamInvites/PendingInvites";
import useInviteStyles from "../TeamInvites/styles";

// Backend ListUsersService usa limit=9 por página. Manter alinhado pra que
// totalPages calculado aqui case com a quantidade real retornada.
const PAGE_SIZE = 9;

const reducer = (state, action) => {
  if (action.type === "LOAD_USERS") return action.payload;
  if (action.type === "UPDATE_USERS") {
    const user = action.payload;
    const idx = state.findIndex((u) => u.id === user.id);
    if (idx !== -1) {
      const next = [...state];
      next[idx] = user;
      return next;
    }
    return [user, ...state];
  }
  if (action.type === "DELETE_USER") {
    return state.filter((u) => u.id !== +action.payload);
  }
  if (action.type === "RESET") return [];
  return state;
};

const useStyles = makeStyles((theme) => {
  const dark = theme.palette.type === "dark";
  return {
    root: {
      padding: "32px 28px 0",
      minHeight: "100%",
      [theme.breakpoints.down("sm")]: { padding: "20px 16px 0" },
    },
    // Sentinela pro bug do Chromium em flex column + overflow-y auto:
    // padding/margin no fim do conteúdo não entra na altura scrollável.
    // Um div com height explícito é conteúdo real e é contado. Mesmo fix
    // aplicado em Dashboard/index.js.
    bottomSpacer: { height: 48, flexShrink: 0 },
    header: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-end",
      marginBottom: 16,
      gap: 16,
      flexWrap: "wrap",
    },
    titleWrap: { display: "flex", alignItems: "baseline", gap: 10 },
    headerActions: { display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" },
    title: {
      fontSize: 22,
      fontWeight: 700,
      color: dark ? "#FAFAFA" : "#09090B",
      letterSpacing: "-0.4px",
      margin: 0,
    },
    count: {
      fontSize: 13,
      fontWeight: 500,
      color: dark ? "#71717A" : "#A1A1AA",
      fontVariantNumeric: "tabular-nums",
    },
    primaryBtn: {
      height: 38,
      padding: "0 14px",
      borderRadius: 10,
      border: "none",
      background: "linear-gradient(135deg, #4f7cff 0%, #3b82f6 100%)",
      color: "#fff",
      fontSize: 13,
      fontWeight: 500,
      cursor: "pointer",
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      transition: "transform .12s, opacity .15s",
      "&:hover": { transform: "translateY(-1px)" },
      "&:active": { transform: "translateY(0)" },
      "&:disabled": { opacity: 0.6, cursor: "not-allowed" },
      fontFamily: "inherit",
    },
  };
});

const UsersManagerModern = () => {
  const classes = useStyles();
  const inviteClasses = useInviteStyles();
  const { user: currentUser, impersonateUser } = useContext(AuthContext);
  const canImpersonate = !!currentUser?.super;
  // Cadastrar, editar e excluir: hoje, admin e super, como a API; com a regra
  // nova, team.manage, e a conta de um administrador (que não a própria) também
  // team.manage_access. Quem não gere a equipe muda os próprios dados no Meu perfil.
  const { can } = usePermission();
  const managesTeam = can("team.manage", isAdminUser(currentUser));
  const managesAccess = can("team.manage_access", isAdminUser(currentUser));
  const canTouch = (u) => managesTeam && (u.profile !== "admin" || u.id === currentUser?.id || managesAccess);
  const { refetchBilling } = useBilling();

  const [users, dispatch] = useReducer(reducer, []);
  const [loading, setLoading] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [pageNumber, setPageNumber] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  const [userModalOpen, setUserModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [deletingUser, setDeletingUser] = useState(null);
  const [impersonatingId, setImpersonatingId] = useState(null);
  // Os convites por e-mail que ainda não viraram conta (quem gere a equipe vê e mexe).
  const [invites, setInvites] = useState([]);
  const [inviteOpen, setInviteOpen] = useState(false);

  const socketManager = useContext(SocketContext);
  const debouncedSearch = useDebounce(searchParam, 400);

  const fetchUsers = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/users/", {
        params: { searchParam: debouncedSearch, pageNumber },
      });
      dispatch({ type: "LOAD_USERS", payload: data.users });
      setTotalCount(data.count);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, [debouncedSearch, pageNumber]);

  // Quando a busca muda, volta pra página 1 (evita "página vazia" se o
  // total de páginas diminuir).
  useEffect(() => {
    setPageNumber(1);
  }, [debouncedSearch]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    if (!managesTeam) return undefined;
    let alive = true;
    api
      .get("/invites")
      .then(({ data }) => alive && setInvites(Array.isArray(data) ? data : []))
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [managesTeam]);

  // O convite pendente reserva a vaga: a contagem de assentos muda com ele.
  const handleInviteSent = useCallback((invite) => {
    setInvites((current) => [invite, ...current.filter((item) => item.id !== invite.id)]);
    toast.success(i18n.t("users.invites.sent", { email: invite.email }));
    refetchBilling && refetchBilling();
  }, [refetchBilling]);

  // Reenviar o vencido volta a reservar a vaga: a contagem de assentos muda também.
  const handleInviteUpdated = useCallback((invite) => {
    setInvites((current) => current.map((item) => (item.id === invite.id ? invite : item)));
    refetchBilling && refetchBilling();
  }, [refetchBilling]);

  const handleInviteRemoved = useCallback((inviteId) => {
    setInvites((current) => current.filter((item) => item.id !== inviteId));
    refetchBilling && refetchBilling();
  }, [refetchBilling]);

  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    const socket = socketManager.getSocket(companyId);
    socket.on(`company-${companyId}-user`, (data) => {
      if (data.action === "update" || data.action === "create") {
        dispatch({ type: "UPDATE_USERS", payload: data.user });
      }
      if (data.action === "delete") {
        dispatch({ type: "DELETE_USER", payload: +data.userId });
      }
    });
    return () => socket.disconnect();
  }, [socketManager]);

  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  const handleOpenCreate = useCallback(() => {
    setSelectedUser(null);
    setUserModalOpen(true);
  }, []);

  const handleEdit = useCallback((user) => {
    setSelectedUser(user);
    setUserModalOpen(true);
  }, []);

  const handleDeleteClick = useCallback((user) => {
    setDeletingUser(user);
  }, []);

  const handleCloseModal = useCallback(() => {
    setUserModalOpen(false);
    setTimeout(() => setSelectedUser(null), 250);
  }, []);

  const handleUserSaved = useCallback(async () => {
    await fetchUsers();
    // Atualiza a contagem de assentos do grupo p/ o botão refletir o novo limite.
    refetchBilling && refetchBilling();
  }, [fetchUsers, refetchBilling]);

  const handleUserDeleted = async () => {
    setDeletingUser(null);
    await fetchUsers();
    refetchBilling && refetchBilling();
  };

  const handleImpersonate = async (user) => {
    if (impersonatingId) return;
    setImpersonatingId(user.id);
    try {
      await impersonateUser(user.id);
      // impersonateUser dispara window.location.assign — não precisa resetar.
    } catch (e) {
      setImpersonatingId(null);
      toastError(e);
    }
  };

  return (
    <div className={classes.root}>
      <UserDeleteImpactModal
        open={!!deletingUser}
        userId={deletingUser?.id}
        userName={deletingUser?.name}
        onClose={() => setDeletingUser(null)}
        onDeleted={handleUserDeleted}
      />

      <div className={classes.header}>
        <div className={classes.titleWrap}>
          <h1 className={classes.title}>{i18n.t("users.title")}</h1>
          <span className={classes.count}>{totalCount}</span>
        </div>
        {/* O limite do plano é conferido no servidor: passou dele, o aviso do limite oferece os planos (PlanLimitPrompt). */}
        {managesTeam && (
          <div className={classes.headerActions}>
            <button type="button" className={inviteClasses.headerButton} onClick={() => setInviteOpen(true)}>
              <Mail size={16} strokeWidth={2} />
              {i18n.t("users.invites.button")}
            </button>
            <button type="button" className={classes.primaryBtn} onClick={handleOpenCreate}>
              <Plus size={16} strokeWidth={2} />
              {i18n.t("users.buttons.add")}
            </button>
          </div>
        )}
      </div>

      {managesTeam && <PendingInvites invites={invites} onUpdate={handleInviteUpdated} onRemove={handleInviteRemoved} />}

      <UsersTableSearch
        value={searchParam}
        onChange={setSearchParam}
        placeholder={i18n.t("contacts.searchPlaceholder")}
      />

      <UsersTable
        users={users}
        loading={loading}
        currentUserId={currentUser?.id}
        canImpersonate={canImpersonate}
        impersonatingId={impersonatingId}
        onImpersonate={handleImpersonate}
        onEdit={handleEdit}
        canEdit={canTouch}
        onDelete={managesTeam ? handleDeleteClick : undefined}
        canDelete={(u) => u.id !== currentUser?.id && canTouch(u)}
        searchActive={!!debouncedSearch}
      />

      <UsersTablePagination
        current={pageNumber}
        total={totalPages}
        onChange={setPageNumber}
      />

      <div className={classes.bottomSpacer} aria-hidden="true" />

      <UserEditorModal
        open={userModalOpen}
        onClose={handleCloseModal}
        onSaved={handleUserSaved}
        selectedUser={selectedUser}
      />

      {managesTeam && <InviteDialog open={inviteOpen} onClose={() => setInviteOpen(false)} onSent={handleInviteSent} />}
    </div>
  );
};

export default UsersManagerModern;
